import React, { useEffect, Suspense, lazy, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "./store";
import { loginSuccess } from "./store/slices/authSlice";
import { Toaster } from "react-hot-toast";
import { AnimatePresence } from "framer-motion";
import { ThemeProvider } from "./theme/ThemeProvider";
import { SidePanelProvider } from "./contexts/SidePanelContext";
import MainLayout from "./layouts/MainLayout";
import AuthLayout from "./layouts/AuthLayout";
import PrivateRoute from "./components/auth/PrivateRoute";
import SplashScreen from "./components/common/SplashScreen";
import LoadingSpinner from "./components/common/LoadingSpinner";
import { useAuthInit } from "./hooks/useAuthInit";
import Login from "./pages/Login";
import WorkerLoginPage from "./pages/WorkerLoginPage";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Orders from "./feature/orders/Orders";
import CreateOrder from "./feature/orders/CreateOrder";
import Sites from "./feature/site/Sites.tsx";
import Settings from "./pages/Settings";
import Users from "./feature/user/Users";
import { StagesPage as Stages } from "./feature/stageModule";
import Branches from "./feature/branch/Branches";
import BranchDetailsPage from "./feature/branch/BranchDetailsPage";
import Customers from "./feature/customer/Customers";
import NotFound from "./pages/NotFound";
import OrderDetailsPage from "./feature/orders/OrderDetailsPage";
import EditOrderPage from "./feature/orders/enhanced/EditOrderPage";
import ChangePassword from "./pages/ChangePassword";
import LogoShowcase from "./components/common/LogoShowcase";
import TimelineTestPage from "./pages/TimelineTestPage";
import MobileCameraTest from "./pages/MobileCameraTest";
import UserDetails from "./feature/user/UserDetails";
import CustomerDetails from "./feature/customer/CustomerDetails";
import RbacManagement from "./feature/rbac/RbacManagement";
import RolePermissions from "./pages/RolePermissions";
import PlaceOrderModal from "./feature/site/PlaceOrderModal.tsx";
import CreateOrderPage from "./feature/orders/enhanced/CreateOrderPage";
import TemporaryNumberingTestPage from "./pages/TemporaryNumberingTestPage";
import ReportMenu from "./components/reports/ReportMenu.tsx";
import { Cities } from "./master/city/Cities.tsx";
import Colors from "./master/color/Colors.tsx";
import RoleTypes from "./master/roleType/RoleTypes.tsx";
import States from "./master/state/States.tsx";
import { PermissionGuard } from "./components/common/PermissionGuard";
import SupervisorLogin from "./pages/SupervisorLogin";
import DispatchScreen from "./pages/DispatchScreen";
import DriverDeliveryScreen from "./pages/DriverDeliveryScreen";
import DispatchDetails from "./pages/DispatchDetails.tsx";
import Location from "./master/location/Location.tsx"
import ICMSettings from "./components/settings/ICMSettings.tsx"
import NetworkOff from "./pages/NetworkOff.tsx";
import BackendEnvBanner from "./components/common/BackendEnvBanner";
import Set from "./master/set/Sets.tsx"
import Item from "./master/item/Item.tsx"
import ItemSubtypeDefinitions from "./master/itemSubtypeDefinition/ItemSubtypeDefinitions.tsx"

// Pages - Lazy loaded for performance
const Profile = lazy(() => import("./pages/Profile"));
const Masters = lazy(() => import("./pages/Masters"));

// Master Components
const WorkflowManager = lazy(
  () => import("./components/workflow/WorkflowManagerWrapper")
);
const WorkflowManagement = lazy(
  () => import("./feature/workflow/WorkflowManagement")
);
const BranchWorkflowManager = lazy(
  () => import("./components/settings/BranchWorkflowManager")
);
const WorkerDashboard = lazy(
  () => import("./components/worker/WorkerDashboard")
);
const BranchManager = lazy(() => import("./feature/branch/BranchManager"));
const RealTimeWorkflowDashboard = lazy(
  () => import("./components/dashboard/RealTimeWorkflowDashboard")
);
const MobileStageCompletionPage = lazy(
  () => import("./pages/MobileStageCompletionPage")
);
const WorkerMobileInterface = lazy(
  () => import("./components/worker/WorkerMobileInterface")
);
const AppWorker = lazy(() => import("./AppWorker"));
const AppWorker2 = lazy(() => import("./AppWorker2"));
const AppSupervisor = lazy(() => import("./AppSupervisor"));
const AdminStageStatusPage = lazy(() => import("./pages/AdminStageStatusPage"));
const ReportsPage = lazy(() => import("./pages/ReportsPage"));
const CountMasterDashboard = lazy(() => import("./components/countMaster/CountMasterDashboard"));
import NotificationSettings from "./pages/Notification.tsx";
import LeadsDashboard from './feature/leads/components/Dashboard/Dashboard';
import LeadDetail from './feature/leads/components/modules/Lead/LeadDetail';
import ContactDetail from './feature/leads/components/modules/Contact/ContactDetail';
import ContactPage from './feature/leads/components/pages/ContactPage/ContactPage';
const LeadStageMasterAdmin = lazy(() => import('./feature/leads/components/admin/LeadStageMasterAdmin'));
import DailySalesDashboard from './features/sales-executive/dashboard1/DailySalesDashboard';

const App: React.FC = () => {
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const location = useLocation();
  const [showSplash, setShowSplash] = useState(true);
  const { isInitializing } = useAuthInit();
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Theme is handled by the theme slice, no need for duplicate handling here

  const handleSplashComplete = () => {
    setShowSplash(false);
  };

  // Show splash screen on first load
  if (showSplash) {
    return (
      <ThemeProvider>
        <SplashScreen onComplete={handleSplashComplete} duration={3000} />
      </ThemeProvider>
    );
  }

  // Show loading spinner while initializing auth
  if (isInitializing) {
    return (
      <ThemeProvider>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <LoadingSpinner />
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <SidePanelProvider>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3000,
            style: {
              background: "#333",
              color: "#fff",
            },
            success: {
              iconTheme: {
                primary: "#22c55e",
                secondary: "#fff",
              },
            },
            error: {
              iconTheme: {
                primary: "#ef4444",
                secondary: "#fff",
              },
            },
          }}
        />
        <BackendEnvBanner />

        {isOnline ? (
          <AnimatePresence mode="wait">
            <Suspense fallback={<LoadingSpinner />}>
              <Routes location={location}>
                {/* Public routes */}
                <Route
                  path="/login"
                  element={
                    <AuthLayout title="" subtitle="">
                      <Login />
                    </AuthLayout>
                  }
                />
                <Route
                  path="/forgot-password"
                  element={
                    <AuthLayout
                      title="Reset Password"
                      subtitle="We'll help you get back to your account"
                    >
                      <ForgotPassword />
                    </AuthLayout>
                  }
                />
                <Route
                  path="/reset-password"
                  element={
                    <AuthLayout
                      title="Set New Password"
                      subtitle="Create a secure password for your account"
                    >
                      <ResetPassword />
                    </AuthLayout>
                  }
                />

                {/* Worker Login Route */}
                <Route
                  path="/infinity-app"
                  element={<WorkerLoginPage />}
                />

                {/* supervisor login route */}
                <Route
                  path="/supervisor-login"
                  element={<SupervisorLogin />}
                />

                {/* <Route path="/supervisor/:id" element={<DispatchDetails />} /> */}


                {/* Protected routes */}
                <Route
                  path="/"
                  element={
                    <PrivateRoute>
                      <MainLayout />
                    </PrivateRoute>
                  }
                >
                  <Route path="orders" element={<Orders />} />
                  <Route index element={<Orders />} />

                  {/* <Route path="orders/create" element={<CreateOrder />} /> */}
                  {/* <Route path="orders/create" element={<PlaceOrderModal />} /> */}
                  <Route path="orders/:id" element={<OrderDetailsPage />} />
                  <Route path="orders/edit/:id" element={<EditOrderPage />} />
                  <Route path="sites" element={<Sites />} />
                  <Route
                    path="orders/new/:customerId"
                    element={<CreateOrderPage />}
                  />
                  <Route
                    path="orders/new/:customerId/:siteId"
                    element={<CreateOrderPage />}
                  />
                  <Route path="orders/new" element={<CreateOrderPage />} />
                  <Route path="orders/enhanced/:customerId/:siteId" element={<CreateOrderPage />} />
                  <Route path="orders/enhanced/:customerId" element={<CreateOrderPage />} />
                  <Route path="orders/enhanced" element={<CreateOrderPage />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="users" element={<Users />} />
                  <Route
                    path="masters/stages"
                    element={
                      <PermissionGuard module="stages" action="read" fallback={<NotFound />}>
                        <Stages />
                      </PermissionGuard>
                    }
                  />
                  <Route path="users/:id" element={<UserDetails />} />
                  <Route path="branches" element={<Branches />} />
                  <Route
                    path="branches/:branchId"
                    element={<BranchDetailsPage />}
                  />
                  <Route path="customers" element={<Customers />} />
                  <Route path="customers/:id" element={<CustomerDetails />} />
                  <Route path="masters/rbac" element={<RbacManagement />} />
                  <Route path="masters/icm" element={<ICMSettings />} />
                  <Route path="masters/notification" element={<NotificationSettings />} />
                  <Route path="role-permissions" element={<RolePermissions />} />
                  <Route
                    path="profile"
                    element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <Profile />
                      </Suspense>
                    }
                  />
                  <Route path="change-password" element={<ChangePassword />} />
                  <Route path="logo-showcase" element={<LogoShowcase />} />
                  <Route path="timeline-test" element={<TimelineTestPage />} />
                  <Route path="mobile-camera-test" element={<MobileCameraTest />} />
                  <Route path="temporary-numbering-test" element={<TemporaryNumberingTestPage />} />
                  <Route path="dispatch" element={<DispatchScreen />} />
                  <Route path="driver-delivery" element={<DriverDeliveryScreen />} />
                  <Route
                    path="masters"
                    element={
                      <PermissionGuard module="master" action="read" fallback={<NotFound />}>
                        <Suspense fallback={<LoadingSpinner />}>
                          <Masters />
                        </Suspense>
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="masters/workflow"
                    element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <WorkflowManager />
                      </Suspense>
                    }
                  />
                  <Route
                    path="workflow"
                    element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <WorkflowManagement />
                      </Suspense>
                    }
                  />
                  <Route
                    path="workflow-management"
                    element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <WorkflowManager />
                      </Suspense>
                    }
                  />
                  <Route
                    path="worker"
                    element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <WorkerDashboard />
                      </Suspense>
                    }
                  />
                  <Route
                    path="branch-workflows"
                    element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <BranchWorkflowManager workflows={[]} />
                      </Suspense>
                    }
                  />

                  <Route
                    path="realtime-dashboard"
                    element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <RealTimeWorkflowDashboard />
                      </Suspense>
                    }
                  />
                  <Route
                    path="mobile-stage-completion"
                    element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <MobileStageCompletionPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="worker-mobile"
                    element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <WorkerMobileInterface />
                      </Suspense>
                    }
                  />
                  <Route
                    path="masters/branch"
                    element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <BranchManager />
                      </Suspense>
                    }
                  />
                  <Route
                    path="masters/state"
                    element={
                      <PermissionGuard module="master" action="read" fallback={<NotFound />}>
                        <PermissionGuard module="states" action="read" fallback={<NotFound />}>
                          <Suspense fallback={<LoadingSpinner />}>
                            <States />
                          </Suspense>
                        </PermissionGuard>
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="masters/city"
                    element={
                      <PermissionGuard module="master" action="read" fallback={<NotFound />}>
                        <PermissionGuard module="cities" action="read" fallback={<NotFound />}>
                          <Suspense fallback={<LoadingSpinner />}>
                            <Cities />
                          </Suspense>
                        </PermissionGuard>
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="masters/color"
                    element={
                      <PermissionGuard module="master" action="read" fallback={<NotFound />}>
                        <PermissionGuard module="colors" action="read" fallback={<NotFound />}>
                          <Suspense fallback={<LoadingSpinner />}>
                            <Colors />
                          </Suspense>
                        </PermissionGuard>
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="masters/location"
                    element={
                      <PermissionGuard module="master" action="read" fallback={<NotFound />}>
                        <PermissionGuard module="colors" action="read" fallback={<NotFound />}>
                          <Suspense fallback={<LoadingSpinner />}>
                            <Location />
                          </Suspense>
                        </PermissionGuard>
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="masters/set"
                    element={
                      <PermissionGuard module="master" action="read" fallback={<NotFound />}>
                        <PermissionGuard module="colors" action="read" fallback={<NotFound />}>
                          <Suspense fallback={<LoadingSpinner />}>
                            <Set />
                          </Suspense>
                        </PermissionGuard>
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="masters/item"
                    element={
                      <PermissionGuard module="items" action="read" fallback={<NotFound />}>
                        <PermissionGuard module="master" action="read" fallback={<NotFound />}>
                          <Suspense fallback={<LoadingSpinner />}>
                            <Item />
                          </Suspense>
                        </PermissionGuard>
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="masters/role-types"
                    element={
                      <PermissionGuard module="master" action="read" fallback={<NotFound />}>
                        <Suspense fallback={<LoadingSpinner />}>
                          <RoleTypes />
                        </Suspense>
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="masters/item-subtype-definitions"
                    element={
                      <PermissionGuard module="item_subtype_definitions" action="read" fallback={<NotFound />}>
                        <PermissionGuard module="master" action="read" fallback={<NotFound />}>
                          <Suspense fallback={<LoadingSpinner />}>
                            <ItemSubtypeDefinitions />
                          </Suspense>
                        </PermissionGuard>
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="admin/stage-status"
                    element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <AdminStageStatusPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="reports"
                    element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <ReportsPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="reports/:reportId"
                    element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <ReportsPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="masters/count-master"
                    element={
                      <PermissionGuard module="count_master" action="read" fallback={<NotFound />}>
                        <Suspense fallback={<LoadingSpinner />}>
                          <CountMasterDashboard />
                        </Suspense>
                      </PermissionGuard>
                    }
                  />
                  <Route path="leads" element={<LeadsDashboard />} />
                  <Route path="leads/:id" element={<LeadDetail />} />
                  <Route path="contacts" element={<ContactPage />} />
                  <Route path="contacts/:id" element={<ContactDetail />} />
                  <Route path="dashboard1" element={<DailySalesDashboard />} />
                  {/* <Route path="dashboard2" element={<SalesExecutive />} /> */}
                  <Route
                    path="masters/lead-stages"
                    element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <LeadStageMasterAdmin />
                      </Suspense>
                    }
                  />


                </Route>

                {/* Worker Routes - Completely isolated from main app layout */}
                <Route
                  path="worker/*"
                  element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <AppWorker />
                    </Suspense>
                  }
                />

                {/* Worker2 Routes - Second worker portal for testing */}
                <Route
                  path="worker2/*"
                  element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <AppWorker2 />
                    </Suspense>
                  }
                />

                {/* Supervisor Routes - Completely isolated supervisor dispatch portal */}
                <Route
                  path="supervisor/*"
                  element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <AppSupervisor />
                    </Suspense>
                  }
                />

                {/* Catch all route - redirect to login if not authenticated */}
                {/* Note: Public routes like /worker-login are matched above, so they won't reach this catch-all */}
                <Route
                  path="*"
                  element={
                    isAuthenticated ? (
                      <NotFound />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
              </Routes>
            </Suspense>
          </AnimatePresence>
        ) : (
          <NetworkOff />
        )}
      </SidePanelProvider>
    </ThemeProvider>
  );
};

export default App;
